//------------ IMPORTAÇÕES ------------//
import { useEffect, useContext, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WhatsAppSessoesContext } from "./context/WhatsAppSessoesContext";
import { SidebarContext } from "../../shared/context/SidebarContext";
import {
    loadSessoesService,
    createSessaoService,
    deleteSessaoService,
    getQrCodeService,
    reconnectQrCodeService,
    getStatusService,
    sendTextService,
    leaveAllGroupsService,
} from "./services/sessoesService";
import { Smartphone, Plus, Trash2, RefreshCw, QrCode, Wifi, WifiOff, X, Send, LogOut, BadgeCheck, Server, Globe, Check, MessagesSquare, Disc3 } from "lucide-react";
import Swal from 'sweetalert2';
import { Link, useNavigate } from "react-router-dom";
import GravacaoModal from "../WhatsAppChat/components/GravacaoModal";



//------------ EXPORTAÇÃO DA PÁGINA SESSÕES WHATSAPP ------------//
export default function WhatsAppSessoes() {

    const { sessoes, setSessoes } = useContext(WhatsAppSessoesContext);
    const { isOpen } = useContext(SidebarContext);

    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [showMsgModal, setShowMsgModal] = useState(false);
    const [qrData, setQrData] = useState(null);
    const [qrSessionName, setQrSessionName] = useState("");
    const [nome, setNome] = useState("");
    const [numero, setNumero] = useState("");
    const [provider, setProvider] = useState("whatsmeow");
    const [creating, setCreating] = useState(false);

    // Send message state
    const [msgSessionName, setMsgSessionName] = useState("");
    const [msgNumero, setMsgNumero] = useState("");
    const [msgTexto, setMsgTexto] = useState("");
    const [sending, setSending] = useState(false);
    const [qrPolling, setQrPolling] = useState(false);

    // Gravação de mensagens (chat por sessão)
    const [gravacaoSessao, setGravacaoSessao] = useState(null);
    const navigate = useNavigate();



    //------------ EFEITO INICIAL: CARREGAR SESSÕES ------------//
    // Config Meta legacy removida — agora vive em /whatsapp/oficial (Fase 3).
    useEffect(() => {
        loadSessoesService(setSessoes, setLoading);
    }, []);



    //------------ POLLING: DETECTAR CONEXÃO E FECHAR QR MODAL ------------//
    useEffect(() => {
        if (!showQrModal || !qrSessionName) return;

        setQrPolling(true);
        const interval = setInterval(async () => {
            try {
                const data = await getQrCodeService(qrSessionName);

                // Se whatsmeow retornou que já está conectado
                if (data?.instance?.state === "open") {
                    clearInterval(interval);
                    setQrPolling(false);
                    setShowQrModal(false);
                    setQrData(null);
                    toast.success("✅ WhatsApp conectado com sucesso!");
                    
                    // Atualização Otimista local
                    setSessoes(prev => prev.map(s => 
                        s.session_name === qrSessionName ? { ...s, status: "conectado" } : s
                    ));

                    // Delay a recarga para dar tempo do Webhook do Go atualizar o Postgres
                    setTimeout(() => {
                        loadSessoesService(setSessoes, setLoading);
                    }, 2500);
                    
                    return;
                }

                // Atualizar QR se novo código disponível. O backend devolve o QR
                // como `qr` (GET /qr, /restart) — wppconnect entrega de forma
                // assíncrona, então o polling vai preenchendo conforme chega.
                const novoQr = data?.qr || data?.base64;
                if (novoQr) {
                    setQrData(novoQr);
                }
            } catch (err) {
                // Silenciar erros de polling
            }
        }, 3000);

        return () => {
            clearInterval(interval);
            setQrPolling(false);
        };
    }, [showQrModal, qrSessionName]);



    //------------ FECHAR MODAL DE CRIAÇÃO (reseta state) ------------//
    // Como o modal é render condicional sem desmontar, sem reset o provider/nome/número
    // ficariam lembrados da abertura anterior. Zera tudo de volta ao estado inicial.
    const closeModal = () => {
        setNome("");
        setNumero("");
        setProvider("whatsmeow");
        setShowModal(false);
    };



    //------------ CRIAR SESSÃO ------------//
    const handleCreate = async () => {
        if (!nome.trim()) {
            toast.error("Nome é obrigatório");
            return;
        }
        setCreating(true);
        try {
            const data = await createSessaoService(nome.trim(), numero.trim(), provider);
            toast.success("Sessão criada!");

            // Sessão recém-criada nunca está conectada: abrimos o modal de QR mesmo
            // que o código ainda não tenha vindo (wppconnect entrega assíncrono) —
            // o polling preenche o QR assim que ele chega pelo webhook.
            if (data.session_name) {
                setQrData(data.qr || null);
                setQrSessionName(data.session_name);
                setShowQrModal(true);
            }

            setNome("");
            setNumero("");
            setProvider("whatsmeow");
            setShowModal(false);
            loadSessoesService(setSessoes, setLoading);
        } catch (err) {
            toast.error("Erro ao criar sessão");
            console.error(err);
        } finally {
            setCreating(false);
        }
    };



    //------------ RECONECTAR / NOVO QR ------------//
    const handleReconnect = async (sessionName) => {
        try {
            const data = await reconnectQrCodeService(sessionName);
            // Já conectada: nada de QR.
            if (data?.instance?.state === "open") {
                toast.info("Sessão já está conectada");
                loadSessoesService(setSessoes, setLoading);
                return;
            }
            // Caso contrário abrimos o modal mesmo sem o QR em mãos: no wppconnect
            // ele chega de forma assíncrona e o polling preenche em alguns segundos.
            setQrData(data?.qr || data?.base64 || null);
            setQrSessionName(sessionName);
            setShowQrModal(true);
        } catch (err) {
            toast.error("Erro ao buscar QR Code");
        }
    };

    //------------ DELETAR SESSÃO ------------//
    const handleDelete = async (id) => {
        if (!window.confirm("Deseja realmente excluir esta sessão?")) return;
        try {
            await deleteSessaoService(id);
            toast.success("Sessão excluída!");
            loadSessoesService(setSessoes, setLoading);
        } catch (err) {
            toast.error("Erro ao excluir sessão");
        }
    };

    //------------ SAIR DOS GRUPOS ------------//
    const handleLeaveAllGroups = async (sessionName) => {
        const result = await Swal.fire({
            title: 'Você tem certeza?',
            text: `A sessão "${sessionName}" irá sair ativamente de TODOS os grupos do WhatsApp. Esta ação não pode ser desfeita e pode chamar a atenção de sistemas anti-spam!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#3f3f46',
            confirmButtonText: 'Sim, Sair dos Grupos',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const res = await leaveAllGroupsService(sessionName);
                if (res.status === 'processing') {
                    const tempoSeg = res.total * 2;
                    let restante = tempoSeg;
                    Swal.fire({
                        icon: 'info',
                        title: 'Saída de Grupos em andamento',
                        html: `<p class="text-sm">O WhatsApp está processando a saída de <b>${res.total}</b> grupo(s).</p><p class="text-xs text-gray-400 mt-2">🔄 Processo em andamento (~${restante}s)</p>`,
                        showConfirmButton: true,
                        confirmButtonText: 'Fechar',
                        timer: tempoSeg * 1000,
                        timerProgressBar: true,
                        didOpen: () => {
                            const interval = setInterval(() => {
                                restante = Math.max(0, Math.ceil(Swal.getTimerLeft() / 1000));
                                const el = Swal.getHtmlContainer();
                                if (el) {
                                   const p = el.querySelector('.text-gray-400');
                                   if(p) p.innerHTML = `🔄 Processo em andamento (~${restante}s)`;
                                }
                            }, 5000);
                            Swal.getPopup().__interval = interval;
                        },
                        willClose: () => clearInterval(Swal.getPopup().__interval),
                    });
                } else {
                    toast.success("Comando recebido pelo servidor!");
                }
            } catch (err) {
                toast.error(err.response?.data?.error || "Erro ao iniciar processo.");
                console.error(err);
            }
        }
    };



    //------------ ENVIAR MENSAGEM ------------//
    const handleOpenMsg = (sessionName) => {
        setMsgSessionName(sessionName);
        setMsgNumero("");
        setMsgTexto("");
        setShowMsgModal(true);
    };

    const handleSendMsg = async () => {
        if (!msgNumero.trim() || !msgTexto.trim()) {
            toast.error("Número e mensagem são obrigatórios");
            return;
        }
        setSending(true);
        try {
            await sendTextService(msgSessionName, msgNumero.trim(), msgTexto.trim());
            toast.success("Mensagem enviada!");
            setShowMsgModal(false);
        } catch (err) {
            toast.error("Erro ao enviar mensagem");
            console.error(err);
        } finally {
            setSending(false);
        }
    };



    //------------ RETORNO JSX ------------//
    if (loading) return (
        <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} p-6 mb-16 sm:mb-0 min-h-screen bg-base text-white flex items-center justify-center`}>
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
        </main>
    );

    return (
        <main className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto min-h-screen text-ink-1 mb-16 sm:mb-0`}>

            <header className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 animate-fade-in">
                <div className="space-y-1">
                    <span className="label-overline">WhatsApp</span>
                    <h1 className="display-2 flex items-center gap-3">
                        Sessões
                        <Smartphone className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
                    </h1>
                    <p className="text-ink-3 text-sm">
                        Conexões via QR code (whatsmeow) — para uso oficial via Meta, acesse a aba ao lado.
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Link to="/whatsapp/oficial" className="btn-ghost !py-2.5">
                        <BadgeCheck className="h-4 w-4 text-brand-400" />
                        WhatsApp Oficial (Meta)
                    </Link>
                    <button onClick={() => setShowModal(true)} className="btn-primary !py-2.5">
                        <Plus className="h-4 w-4" />
                        Nova Sessão QR
                    </button>
                </div>
            </header>



            {/* LISTA DE SESSÕES */}
            {sessoes.length === 0 ? (
                <div className="text-center py-16 text-ink-3">
                    <Smartphone className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nenhuma sessão cadastrada</p>
                    <p className="text-sm mt-1">Clique em "Nova Sessão" para conectar um número</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {sessoes.map((sessao) => (
                        <div key={sessao.id} className="bg-surface-1 border border-white/[0.06] rounded-xl p-5">

                            {/* Status + Nome */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    {sessao.status === "conectado" ? (
                                        <Wifi className="w-4 h-4 text-emerald-400 shrink-0" />
                                    ) : (
                                        <WifiOff className="w-4 h-4 text-red-400 shrink-0" />
                                    )}
                                    <h3 className="font-semibold truncate">{sessao.nome}</h3>
                                </div>
                                <span
                                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ml-2 ${sessao.status === "conectado"
                                        ? "bg-emerald-900/50 text-emerald-400"
                                        : "bg-red-900/50 text-red-400"
                                        }`}
                                >
                                    {sessao.status}
                                </span>
                            </div>
                            {sessao.status_detalhe && (
                                <p className="text-[11px] text-amber-400/90 -mt-2 mb-3 leading-snug" title={sessao.status_detalhe}>
                                    ⚠️ Conectada só no servidor (socket morto) — o celular provavelmente desconectou. Clique em reconectar e leia o novo QR.
                                </p>
                            )}

                            {/* Provider badge + gravação */}
                            <div className="mb-3 flex flex-wrap items-center gap-1.5">
                                <span className="chip-neutral">
                                    {(sessao.provider || "whatsmeow") === "wppconnect" ? (
                                        <Globe className="h-3 w-3" />
                                    ) : (
                                        <Server className="h-3 w-3" />
                                    )}
                                    {sessao.provider || "whatsmeow"}
                                </span>
                                {sessao.gravar_mensagens === false ? (
                                    <span className="chip-neutral" title="Mensagens desta sessão não estão sendo gravadas">
                                        <Disc3 className="h-3 w-3" />
                                        Gravação off
                                    </span>
                                ) : (
                                    <span className="chip-positive" title="Mensagens desta sessão são gravadas no chat">
                                        <span className="h-1.5 w-1.5 rounded-full bg-positive-400 animate-glow-pulse" />
                                        Gravando
                                    </span>
                                )}
                                {sessao.gravar_mensagens !== false && sessao.gravar_grupos === "todos" && (
                                    <span className="chip-brand">todos os grupos</span>
                                )}
                                {sessao.gravar_mensagens !== false && sessao.gravar_grupos === "lista" && (
                                    <span className="chip-brand">
                                        +{Array.isArray(sessao.gravar_grupos_lista) ? sessao.gravar_grupos_lista.length : 0} grupos
                                    </span>
                                )}
                            </div>

                            {/* Info */}
                            {sessao.numero && (
                                <p className="text-sm text-ink-3 mb-1">📱 {sessao.numero}</p>
                            )}
                            <p className="text-xs text-ink-5 mb-4 truncate">
                                {sessao.session_name}
                            </p>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2">
                                {sessao.status === "conectado" && (
                                    <button
                                        onClick={() => handleOpenMsg(sessao.session_name)}
                                        className="flex items-center gap-1 bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-400 px-3 py-1.5 rounded-lg text-sm transition"
                                    >
                                        <Send className="w-3.5 h-3.5" />
                                        Enviar
                                    </button>
                                )}
                                {sessao.status !== "conectado" && (
                                    <button
                                        onClick={() => handleReconnect(sessao.session_name)}
                                        className="flex items-center gap-1 bg-surface-2 hover:bg-surface-3 text-white px-3 py-1.5 rounded-lg text-sm transition"
                                    >
                                        <QrCode className="w-3.5 h-3.5" />
                                        Conectar
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(`/whatsapp/chat/${sessao.id}`)}
                                    className="flex items-center gap-1 bg-brand-500/10 hover:bg-brand-500/20 text-brand-300 px-3 py-1.5 rounded-lg text-sm transition"
                                    title="Abrir o chat desta sessão"
                                >
                                    <MessagesSquare className="w-3.5 h-3.5" />
                                    Chat
                                </button>
                                <button
                                    onClick={() => setGravacaoSessao(sessao)}
                                    className="flex items-center gap-1 bg-surface-2 hover:bg-surface-3 text-ink-2 px-3 py-1.5 rounded-lg text-sm transition"
                                    title="Configurar o que gravar nesta sessão"
                                >
                                    <Disc3 className="w-3.5 h-3.5" />
                                    Gravação
                                </button>
                                <div className="flex w-full items-center justify-between gap-2 mt-2 pt-2 border-t border-white/[0.06]">
                                    {sessao.status === "conectado" && (
                                        <button
                                            onClick={() => handleLeaveAllGroups(sessao.session_name)}
                                            className="flex flex-1 items-center justify-center gap-1 bg-orange-900/30 hover:bg-orange-900/50 text-orange-400 px-3 py-1.5 rounded-lg text-sm transition"
                                            title="Sair de todos os grupos do WhatsApp"
                                        >
                                            <LogOut className="w-3.5 h-3.5" />
                                            Sair dos Grupos
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(sessao.id)}
                                        className="flex flex-1 items-center justify-center gap-1 bg-red-900/30 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded-lg text-sm transition"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Excluir
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}



            {/* MODAL: CRIAR SESSÃO */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">Nova Sessão</h2>
                            <button onClick={closeModal} className="text-ink-3 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label id="provider-label" className="block text-sm text-ink-3 mb-2">Engine de conexão</label>
                                <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-labelledby="provider-label" aria-label="Engine de conexão">
                                    {[
                                        { id: "whatsmeow", nome: "Whatsmeow", desc: "Engine Go local", Icon: Server },
                                        { id: "wppconnect", nome: "WPPConnect", desc: "Engine via browser", Icon: Globe },
                                    ].map(({ id, nome: pNome, desc, Icon }) => {
                                        const ativo = provider === id;
                                        return (
                                            <button
                                                key={id}
                                                type="button"
                                                role="radio"
                                                aria-checked={provider === id}
                                                onClick={() => setProvider(id)}
                                                className={`relative text-left rounded-xl border p-3 transition ${ativo
                                                    ? "border-brand-500/60 bg-brand-500/10 shadow-glow-sm"
                                                    : "border-white/[0.08] bg-surface-2 hover:border-white/[0.16]"
                                                    }`}
                                            >
                                                {ativo && (
                                                    <span className="absolute top-2 right-2 h-4 w-4 rounded-full bg-brand-500 grid place-items-center">
                                                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                                    </span>
                                                )}
                                                <span className={`inline-grid h-8 w-8 place-items-center rounded-lg mb-2 ring-1 ${ativo
                                                    ? "bg-brand-500/15 ring-brand-500/25"
                                                    : "bg-surface-3 ring-white/[0.06]"
                                                    }`}>
                                                    <Icon className={`h-4 w-4 ${ativo ? "text-brand-300" : "text-ink-3"}`} />
                                                </span>
                                                <p className="text-sm font-medium text-ink-1">{pNome}</p>
                                                <p className="text-xs text-ink-4 mt-0.5">{desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="sessao-nome" className="block text-sm text-ink-3 mb-1">Nome da sessão *</label>
                                <input
                                    id="sessao-nome"
                                    type="text"
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    placeholder="Ex: Atendimento, Marketing..."
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                                />
                            </div>

                            <div>
                                <label htmlFor="sessao-numero" className="block text-sm text-ink-3 mb-1">Número (opcional)</label>
                                <input
                                    id="sessao-numero"
                                    type="text"
                                    value={numero}
                                    onChange={(e) => setNumero(e.target.value)}
                                    placeholder="Ex: 5511999999999"
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={closeModal}
                                className="flex-1 bg-surface-2 hover:bg-surface-3 text-white py-2 rounded-lg transition"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={creating}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2 rounded-lg transition font-medium"
                            >
                                {creating ? "Criando..." : "Criar e Conectar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* MODAL: GRAVAÇÃO DE MENSAGENS */}
            {gravacaoSessao && (
                <GravacaoModal
                    sessao={gravacaoSessao}
                    onClose={() => setGravacaoSessao(null)}
                    onSaved={(cfg) => {
                        setSessoes((prev) => prev.map((s) => (s.id === gravacaoSessao.id ? { ...s, ...cfg } : s)));
                    }}
                />
            )}



            {/* MODAL: QR CODE */}
            {showQrModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 w-full max-w-sm text-center">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold">Escaneie o QR Code</h2>
                            <button
                                onClick={() => { setShowQrModal(false); setQrData(null); }}
                                className="text-ink-3 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-ink-3 mb-2">
                            Abra o WhatsApp → Aparelhos Conectados → Conectar
                        </p>

                        {qrPolling && (
                            <div className="flex items-center gap-2 justify-center mb-3">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                </span>
                                <span className="text-xs text-emerald-400">Aguardando leitura do QR...</span>
                            </div>
                        )}

                        {qrData ? (
                            <div className="bg-white p-4 rounded-xl inline-block mb-4">
                                <img
                                    src={qrData.startsWith("data:") ? qrData : `data:image/png;base64,${qrData}`}
                                    alt="QR Code"
                                    className="w-56 h-56 sm:w-64 sm:h-64"
                                />
                            </div>
                        ) : (
                            <div className="w-56 h-56 sm:w-64 sm:h-64 bg-surface-2 rounded-xl mx-auto mb-4 flex items-center justify-center">
                                <RefreshCw className="w-8 h-8 text-ink-4 animate-spin" />
                            </div>
                        )}
                    </div>
                </div>
            )}



            {/* MODAL: ENVIAR MENSAGEM */}
            {showMsgModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">Enviar Mensagem</h2>
                            <button onClick={() => setShowMsgModal(false)} className="text-ink-3 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="msg-numero" className="block text-sm text-ink-3 mb-1">Número do destinatário *</label>
                                <input
                                    id="msg-numero"
                                    type="text"
                                    value={msgNumero}
                                    onChange={(e) => setMsgNumero(e.target.value)}
                                    placeholder="Ex: 5511999999999"
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                                />
                            </div>

                            <div>
                                <label htmlFor="msg-texto" className="block text-sm text-ink-3 mb-1">Mensagem *</label>
                                <textarea
                                    id="msg-texto"
                                    value={msgTexto}
                                    onChange={(e) => setMsgTexto(e.target.value)}
                                    placeholder="Digite sua mensagem..."
                                    rows={4}
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500 resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowMsgModal(false)}
                                className="flex-1 bg-surface-2 hover:bg-surface-3 text-white py-2 rounded-lg transition"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSendMsg}
                                disabled={sending}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2 rounded-lg transition font-medium flex items-center justify-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                {sending ? "Enviando..." : "Enviar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL Meta legacy removido na Fase 3 — config agora em /whatsapp/oficial */}
        </main>
    );
}
